<script lang="ts">
  import { dndzone } from "svelte-dnd-action";
  import type {} from "@movabletype/mt-rich-text-editor/mt-rich-text-editor";
  import { flipDurationMs, dropTargetStyle } from "./common";
  import moveIcon from "../asset/move.svg?raw";
  import trashIcon from "../asset/trash.svg?raw";

  const { textarea } = $props<{
    textarea: HTMLTextAreaElement;
  }>();

  type BlockItem = { value: string; label: string };
  type BlockInternalItem = { id: string; value: string; label: string; isBlockItem: true };

  const availableBlocks: BlockInternalItem[] = convertToItems(
    JSON.parse(textarea.getAttribute("data-available-blocks") ?? "[]")
  );

  function convertToItems(data: (BlockItem | string)[]): BlockInternalItem[] {
    return data
      .map((item) => {
        if (typeof item === "string") {
          const block = availableBlocks.find((block) => block.value === item);
          if (!block) {
            return undefined;
          }
          return {
            id: block.value,
            value: block.value,
            label: block.label,
            isBlockItem: true,
          };
        } else {
          return {
            id: item.value,
            value: item.value,
            label: item.label,
            isBlockItem: true,
          };
        }
      })
      .filter((item) => item !== undefined) as BlockInternalItem[];
  }

  function convertToData(items: BlockInternalItem[]): (BlockItem | string)[] {
    // TODO: In a future version, we'll allow you to specify the label as well.
    // return items.map((item) => ({
    //   value: item.value,
    //   label: item.label,
    // }));
    return items.map((item) => item.value);
  }

  let blocksItems = $state(convertToItems(JSON.parse(textarea.value)));

  let tmpUnusedItems = $state<BlockInternalItem[] | undefined>(undefined);
  const unusedItems = $derived(
    tmpUnusedItems ?? availableBlocks.filter((item) => !getUsedItems(blocksItems).has(item.value))
  );

  function getUsedItems(items: BlockInternalItem[]): Set<string> {
    return new Set(items.map((item) => item.value));
  }

  // Handle DnD events for current blocks
  function handleDndConsider({ detail: { items } }: CustomEvent<{ items: BlockInternalItem[] }>) {
    if (items.some((item) => !item.isBlockItem)) {
      return;
    }
    blocksItems = items;
  }

  function handleDndFinalize({ detail: { items } }: CustomEvent<{ items: BlockInternalItem[] }>) {
    if (items.some((item) => !item.isBlockItem)) {
      return;
    }
    blocksItems = items;
  }

  // Handle DnD events for available blocks
  function handleAvailableDndConsider({
    detail: { items },
  }: CustomEvent<{ items: BlockInternalItem[] }>) {
    if (items.some((item) => !item.isBlockItem)) {
      return;
    }
    tmpUnusedItems = items;
  }

  function handleAvailableDndFinalize() {
    tmpUnusedItems = undefined;
  }

  // Remove block
  function handleRemoveBlock(id: string) {
    blocksItems = blocksItems.filter((item) => item.id !== id);
  }

  // Update label
  // TODO: In a future version, we'll allow you to specify the label as well.
  // function handleUpdateLabel(id: string, newLabel: string) {
  //   blocksItems = blocksItems.map((item) => (item.id === id ? { ...item, label: newLabel } : item));
  // }

  $effect(() => {
    textarea.value = JSON.stringify(convertToData(blocksItems));
  });
</script>

<div class="mt-rich-text-editor-blocks-settings">
  <!-- Current blocks -->
  <div class="mt-rich-text-editor-blocks-settings-current">
    <section
      use:dndzone={{
        items: blocksItems,
        flipDurationMs,
        dropTargetStyle,
        dragDisabled: false,
        dropFromOthersDisabled: false,
      }}
      onconsider={handleDndConsider}
      onfinalize={handleDndFinalize}
    >
      {#each blocksItems as item (item.id)}
        <div class="mt-rich-text-editor-blocks-settings-item">
          <div class="mt-rich-text-editor-blocks-settings-item-content">
            <span class="mt-rich-text-editor-blocks-settings-move">
              {@html moveIcon}
            </span>
            <span class="label">
              {item.label}
            </span>
            <!--
            <input
              type="text"
              value={item.label}
              oninput={(e) => handleUpdateLabel(item.id, e.currentTarget.value)}
            />
            -->
            <span class="mt-rich-text-editor-blocks-settings-value">{item.value}</span>
          </div>
          <button
            class="mt-rich-text-editor-blocks-settings-remove"
            type="button"
            onclick={() => handleRemoveBlock(item.id)}
            title={window.trans("Remove")}
            aria-label={window.trans("Remove")}
          >
            {@html trashIcon}
          </button>
        </div>
      {/each}
    </section>
  </div>

  {#if unusedItems.length > 0 || tmpUnusedItems}
    <!-- Available blocks -->
    <div class="mt-rich-text-editor-blocks-settings-available">
      <h4>{window.trans("Available Paragraph Styles")}</h4>
      <section
        use:dndzone={{
          items: unusedItems,
          flipDurationMs,
          dropTargetStyle,
          dragDisabled: false,
          dropFromOthersDisabled: false,
        }}
        onconsider={handleAvailableDndConsider}
        onfinalize={handleAvailableDndFinalize}
      >
        {#each unusedItems as item (item.id)}
          <div class="mt-rich-text-editor-blocks-settings-item">
            <span class="mt-rich-text-editor-blocks-settings-move">
              {@html moveIcon}
            </span>
            <span class="mt-rich-text-editor-blocks-settings-label">{item.label}</span>
            <span class="mt-rich-text-editor-blocks-settings-value">{item.value}</span>
          </div>
        {/each}
      </section>
    </div>
  {/if}
</div>

<style>
  .mt-rich-text-editor-blocks-settings {
    padding: 1rem;
  }

  .mt-rich-text-editor-blocks-settings-available,
  .mt-rich-text-editor-blocks-settings-current {
    margin-bottom: 2rem;
  }

  .mt-rich-text-editor-blocks-settings-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.5rem;
    margin-bottom: 0.5rem;
    cursor: move;
    border: 1px solid #ccc;
    border-radius: 4px;
  }

  .mt-rich-text-editor-blocks-settings-item-content {
    display: flex;
    gap: 0.5rem;
    align-items: center;
  }

  .mt-rich-text-editor-blocks-settings-value {
    color: #666;
    font-size: 0.9em;
  }

  h4 {
    margin: 0 0 1rem;
    font-size: 1rem;
    color: #666;
  }

  .mt-rich-text-editor-blocks-settings-move {
    width: 20px;
    height: 20px;
    padding: 0;
    border: none;
    background: none;
    color: #666;
    font-size: 16px;
    line-height: 1;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transform: scale(0.75);
  }

  .mt-rich-text-editor-blocks-settings-remove {
    width: 20px;
    height: 20px;
    padding: 0;
    border: none;
    background: none;
    color: #666;
    font-size: 16px;
    line-height: 1;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transform: scale(0.75);
  }

  .mt-rich-text-editor-blocks-settings-remove:hover {
    color: #c00;
  }

  button:not(.mt-rich-text-editor-blocks-settings-remove) {
    padding: 0.25rem 0.5rem;
    border: 1px solid #ddd;
    background: #f8f8f8;
    border-radius: 3px;
    color: #666;
    cursor: pointer;
    font-size: 0.9em;
  }

  .label {
    padding: 0.25rem;
  }

  .mt-rich-text-editor-blocks-settings-available section {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 0.5rem;
  }

  .mt-rich-text-editor-blocks-settings-label {
    flex: 1;
    font-size: 0.9em;
  }
</style>
